import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-regular-svg-icons'

import { useSelector } from 'src/store/redux.store'

import { CATEGORY, EVENT, DataLayer, GoogleAnalyticsDataLayer } from 'src/utils/google-analytics'
import { ReactComponent as NotificationSVG } from '../../assets/menu-notification.svg'
import { useToggleHotDropdown } from '../../hooks/use-toggle-hot-dropdown'
import { useResponsiveHotDropdown } from '../../hooks/use-responsive-hot-dropdown'
import MenuNotificationItem from './notification-item.component'
import { useNotification, NotificationProvider } from './notification.service'

import './notification.styles.scss'

const Notification = ({ tabIndex }) => {
  const user = useSelector(state => state.user)
  const { t } = useTranslation()
  const hotDropdownRef = useRef<JSX.IntrinsicElements['hot-dropdown']>(null)
  const { get, list: notifications } = useNotification()

  useEffect(() => {
    get.all()
  }, [])

  const { state: isDropdownClosed, toggle } = useToggleHotDropdown(hotDropdownRef)

  useResponsiveHotDropdown(hotDropdownRef, {
    maxHeight: 768,
    isMobileFullscreen: true
  })

  const unreadMessagesCounter = notifications.reduce((acc, val) => acc + Number(!val.read), 0)

  function sendDataLayer() {
    const dataLayer = new DataLayer(
      EVENT.INTERACTION,
      CATEGORY.UNIVERSAL_BAR,
      'Click:Notification_bell',
      'Notification_bell'
    )

    GoogleAnalyticsDataLayer.sendPageInteraction(dataLayer)
  }

  function handleUserKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      toggle()

      /**
       * We have a delay in the dropdown state since
       * it is set in useEffect, the next tick;
       *
       * here we need to verify if the dropdown is open
       * (that is the state before the toggle function runs)
       */
      if (!isDropdownClosed) {
        sendDataLayer()
      }
    }
  }

  const isEmptyState = notifications.length === 0

  return (
    user &&
    notifications && (
      <hot-dropdown
        ref={hotDropdownRef}
        onClick={isDropdownClosed ? sendDataLayer : void 0}
        onKeyDown={handleUserKeyDown}
        slot="header-actions"
        class="header-notification hot-dropdocked"
        tabIndex={tabIndex}
      >
        <hot-menu-item slot="button" class="_cursor-pointer">
          <div className="hot-application-menu__icon">
            <hot-tooltip content={t('header.menu.general.notifications')} position="bottom" class="_d-flex">
              <NotificationSVG />
              {unreadMessagesCounter ? (
                <span className="header-notification__unread">
                  {unreadMessagesCounter < 100 ? unreadMessagesCounter : '99+'}
                </span>
              ) : null}
            </hot-tooltip>
          </div>
        </hot-menu-item>

        <div slot="menu" className="_text-gray-600" id="menu-notification-items">
          <div className="_p-4">
            <div className="_d-flex _align-items-center _justify-content-between _mb-2">
              <h5 className="_mb-0 _text-3 _text-gray-700">
                {!isEmptyState && t('header.menu.general.notifications')}
              </h5>
              <div className="header-notification__close">
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </div>

            <div className="header-notification__items">
              {notifications.map(item => (
                <MenuNotificationItem key={item.label} {...item} />
              ))}

              {isEmptyState && (
                <div className="header-notification__empty-state">
                  <i>
                    <NotificationSVG />
                  </i>
                  <h6>{t('notifications.empty_title')}</h6>
                  <p>{t('notifications.empty_body')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </hot-dropdown>
    )
  )
}

const notificationStore = ({ tabIndex }) => (
  <NotificationProvider>
    <Notification tabIndex={tabIndex} />
  </NotificationProvider>
)

export default React.memo(notificationStore)
